import DateRangeFilter from "@components/dashboard/DateRangeFilter";
import BasicInfo from "@components/dashboard/details/BasicInfo";
import FinancialMonthAiPanel from "@components/dashboard/ai/FinancialMonthAiPanel";
import FinancialMonthPickerDialog from "@components/dashboard/ai/FinancialMonthPickerDialog";
import FinancialBarChart from "@components/dashboard/financialProjection/FinancialBarChart";
import DisbursementCoverageCard from "@components/dashboard/financialProjection/DisbursementCoverageCard";
import CashFlowPlanningSourceCard from "@components/dashboard/financialProjection/CashFlowPlanningSourceCard";
import { shouldShowDisbursementCoverage } from "@components/dashboard/financialProjection/cashFlowPlanning";
import { availableCompetences } from "@components/dashboard/financialProjection/availableCompetences";
import UnitAccordion from "@components/dashboard/financialProjection/accordion/UnitAccordion";
import Table from "@components/dashboard/financialProjection/projectionTable/Table";
import Tooltip from "@components/dashboard/tooltip";
import ScheduleSourceStatus from "@components/dashboard/schedule/ScheduleSourceStatus";
import { useDashboardUIActions, useDashboardUIContext } from "@contexts/dashboardUI";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import EscapeHandler from "@utils/escapeHandler";
import { loadStorage, saveStorage } from "@utils/localStorageManager";
import { isPresentationProject } from "@utils/presentationMode";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Select, { components } from 'react-select';

const competenceFormatter = new Intl.DateTimeFormat("pt-BR", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

const financialCompetence = (value) => {
  const match = /^(0[1-9]|1[0-2])\/(\d{4})$/.exec(String(value ?? ""));
  return match ? `${match[2]}-${match[1]}` : null;
};

const aiDeepLink = (searchParams) => {
  const competence = searchParams.get("aiMonth");
  const source = searchParams.get("aiSource");
  if (
    !/^(\d{4})-(0[1-9]|1[0-2])$/.test(String(competence ?? "")) ||
    (source !== "advancement" && source !== "extra_value")
  ) return null;
  return { competence, source };
};

const selectedCompetenceLabel = (competence) => {
  const [year, month] = String(competence ?? "").split("-").map(Number);
  if (!year || !month) return null;
  const label = competenceFormatter.format(new Date(Date.UTC(year, month - 1, 1)));
  return `${label.charAt(0).toUpperCase()}${label.slice(1)} selecionado`;
};

const projectionItems = (project) => [
  ...(Array.isArray(project?.units) ? project.units : []),
  ...["cells", "steps", "substeps", "services"].flatMap((collection) => (
    Object.values(project?.[collection] ?? {}).flatMap((items) => (
      Array.isArray(items) ? items : []
    ))
  )),
];

const extraValuesUnavailable = (project) => projectionItems(project).some((item) => (
  Boolean(item?.monthlyProjection?.errors?.extraValues)
));

const Projection = () => {

  const { barChartDate } = useDashboardUIContext()
  const { activeProject, selectedGraph, projectionTable } = useProjectContext()
  const { selectGraph, selectProject, getCreditors, setDocsToFilter, getAdvancedItems, updateProjectionTableView } = useProjectActions()
  const { closeItemDetailOffcanvas, setIsLoading, setBarChartDate } = useDashboardUIActions();
  const { project_id: routeWorkId } = useParams();
  const [searchParams] = useSearchParams();
  const appliedAiDeepLinksRef = useRef(new Set());
  const [uniqueDocs, setUniqueDocs] = useState([])
  const [docData, setDocData] = useState({
    paidData: [],
    toPayData: [],
  })
  const [docs, setDocs] = useState([])
  const [showMonthAi, setShowMonthAi] = useState(false)
  const [showMonthPicker, setShowMonthPicker] = useState(false)
  const [aiCompetence, setAiCompetence] = useState(null)
  const [aiHighlight, setAiHighlight] = useState(null)
  const presentationReadOnly = isPresentationProject(activeProject)
  const deepLink = aiDeepLink(searchParams)
  const deepLinkKey = deepLink && routeWorkId
    ? `${routeWorkId}:${deepLink.competence}:${deepLink.source}`
    : null
  const hasUnavailableExtraValues = extraValuesUnavailable(activeProject)

  const CustomOption = (props) => {
    const handleIsolateClick = (e) => {
      e.stopPropagation(); // Evita que a opção seja selecionada
      handleIsolate(props.data.value);
      // Força o fechamento do dropdown
      if (props.selectProps && props.selectProps.onMenuClose) {
        props.selectProps.onMenuClose();
      }
    };

    return (
      <components.Option {...props}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <span>{props.data.label}</span>
          <button
            type="button"
            onClick={handleIsolateClick}
            style={{
              marginLeft: '8px',
              padding: '2px 8px',
              fontSize: '12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              background: '#f8f9fa',
              cursor: 'pointer',
              color: '#495057'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#e9ecef';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#f8f9fa';
            }}
          >
            Isolar
          </button>
        </div>
      </components.Option>
    );
  };

  const handleIsolate = (docToIsolate) => {
    // Pega todos os documentos EXCETO o que queremos isolar
    const docsToExclude = uniqueDocs.filter(doc => doc !== docToIsolate);

    // Define os documentos a serem excluídos (todos exceto o isolado)
    setDocs(docsToExclude);

    // Aplica o filtro de exclusão (remove os documentos em docsToExclude)
    const toPayFilter = docData.toPayData.filter(i => docsToExclude.includes(i.doc));
    const paidFilter = docData.paidData.filter(i => docsToExclude.includes(i.doc));

    setDocsToFilter({
      paidData: paidFilter,
      toPayData: toPayFilter,
    });

    // Salva no localStorage os documentos a serem excluídos
    saveStorage(`${activeProject?.building_id}_docsToFilter`, docsToExclude);
  };

  const handleGraph = (i) => {
    let res = ''
    switch (i) {
      // case 0:
      //   res = 'projectionGraph'
      //   break
      case 1:
        res = 'advancementGraph'
        break
      case 2:
        res = 'balanceGraph'
        break
      default:
        res = 'advancementGraph'
        break
    }
    selectGraph(res)
  }

  const resumeItems = (arr) => {
    return arr?.items?.flatMap(i =>
      i.items.flatMap(({ doc, items: j }) =>
        j.map(({ date, value, wbs_code_id_building, wbs_code_id_unit, wbs_code_id_step, wbs_code_id_cell, wbs_code_id_service, wbs_code_id_substep }) => ({
          doc,
          date: date.slice(0, 7).split('-').reverse().join('/'),
          value,
          wbs_code_id_building, wbs_code_id_unit, wbs_code_id_step, wbs_code_id_cell, wbs_code_id_service, wbs_code_id_substep
        }))
      )
    ) || [];
  }

  const aggregate = (arr, dates, docs) => {
    return dates.flatMap(date =>
      docs.map(doc => {
        const itens = arr
          .filter(item => item.date === date && item.doc === doc)
          .map(({ wbs_code_id_building, wbs_code_id_unit, wbs_code_id_step, wbs_code_id_cell, wbs_code_id_service, wbs_code_id_substep, value }) => ({
            wbs_code_id_building, wbs_code_id_unit, wbs_code_id_step, wbs_code_id_cell, wbs_code_id_service, wbs_code_id_substep, value
          }));
        const total = arr
          .filter(item => item.date === date && item.doc === doc)
          .reduce((acc, curr) => acc + curr.value, 0);
        return total > 0 ? { doc, date, value: total, itens } : null;
      }).filter(Boolean)
    );
  }

  const getDocs = async () => {
    setIsLoading(true)
    const paidResponse = await getCreditors('building', activeProject?.wbs_code_ids, 1, barChartDate, true, false, 999999, "", []);
    const toPayResponse = await getCreditors('building', activeProject?.wbs_code_ids, 1, barChartDate, true, true, 999999, "", []);

    const paidItems = resumeItems(paidResponse)
    const toPayItems = resumeItems(toPayResponse)

    const allItems = [...paidItems, ...toPayItems]

    const uniqDocs = [...new Set(allItems.map(item => item.doc))]

    setUniqueDocs(uniqDocs)

    const uniqueDates = [...new Set(allItems.map(item => item.date))];

    const aggregatedPaid = aggregate(paidItems, uniqueDates, uniqDocs)
    const aggregatedToPay = aggregate(toPayItems, uniqueDates, uniqDocs)
    setDocData({
      toPayData: aggregatedToPay,
      paidData: aggregatedPaid
    })
    setIsLoading(false)
    if (docs.length > 0) {
      setIsLoading(true)
      setTimeout(() => {
        setIsLoading(false)
      }, 200)
      const toPayFilter = aggregatedToPay.filter(i => docs.includes(i.doc))
      const paidFilter = aggregatedPaid.filter(i => docs.includes(i.doc))
      setDocsToFilter({
        paidData: paidFilter,
        toPayData: toPayFilter,
      })
    }
  };

  const getAdvance = async () => {
    const res = await getAdvancedItems(activeProject?.building_id, activeProject?.appropriation_level)
  }

  useEffect(() => {
    if (!activeProject?._id) return;
    getDocs();
    getAdvance();
  }, [activeProject, barChartDate]);

  useEffect(() => {
    setShowMonthAi(false);
    setShowMonthPicker(false);
    setAiCompetence(null);
  }, [activeProject?._id]);

  useEffect(() => {
    setAiHighlight((current) => (
      current?.key !== deepLinkKey || activeProject?._id !== routeWorkId
        ? null
        : current
    ));
  }, [activeProject?._id, deepLinkKey, routeWorkId]);

  useEffect(() => {
    if (!deepLink || !deepLinkKey || !activeProject?._id || activeProject._id !== routeWorkId) {
      return;
    }
    if (appliedAiDeepLinksRef.current.has(deepLinkKey)) return;

    appliedAiDeepLinksRef.current.add(deepLinkKey);
    // A ação da IA que originou o link diz respeito só à análise de IA — não
    // deve re-escopar a tabela via barChartDate (ver Contexto do plano).
    setAiCompetence(deepLink.competence);
    setShowMonthAi(true);
    if (deepLink.source === "advancement") {
      selectGraph("advancementGraph");
      updateProjectionTableView(false);
    } else {
      updateProjectionTableView(true);
    }
    setAiHighlight({ ...deepLink, key: deepLinkKey });
  }, [
    activeProject?._id,
    deepLink?.competence,
    deepLink?.source,
    deepLinkKey,
    routeWorkId,
    selectGraph,
    updateProjectionTableView,
  ]);

  useEffect(() => {
    const loadedFilter = loadStorage(`${activeProject?.building_id}_docsToFilter`)
    if (loadedFilter) {
      // setDocsToFilter(loadedFilter);
      setDocs(loadedFilter);
      handleFilter(loadedFilter.map(i => ({ value: i, label: i })));
    }
  }, [uniqueDocs])

  // useEffect(() => {
  //   console.log('mudou a dat')
  //   const toPayFilter = docData.toPayData.filter(i => docs.includes(i.doc))
  //   const paidFilter = docData.paidData.filter(i => docs.includes(i.doc))
  //   setDocsToFilter({
  //     paidData: paidFilter,
  //     toPayData: toPayFilter,
  //   })
  // }, [filterDates])


  if (!activeProject) return <></>

  const handleFilter = (e) => {
    setDocs(e.map(i => i.value))
    // if (e.map(i => i.value).length > docs.length) {
    //   saveStorage('hiddenItens', [
    //     "Desembolso Acumulado",
    //     "Pago Acumulado",
    //     "Adiantamento (Valor adicionado)",
    //     "Receber/Recebidas",
    //     "Adiantamento (Valor retirado)",
    //     "Desembolso com Adiantamento",
    //     "Desembolso",
    //     "Físico Financeiro"
    //   ])
    // }
    const toPayFilter = docData.toPayData.filter(i => e.map(j => j.value).includes(i.doc))
    const paidFilter = docData.paidData.filter(i => e.map(j => j.value).includes(i.doc))
    setDocsToFilter({
      paidData: paidFilter,
      toPayData: toPayFilter,
    })
    saveStorage(`${activeProject?.building_id}_docsToFilter`, e.map(i => i.value));
  }



  return (
    <>
      <BasicInfo />
      <div className="margin-top-24">
        <ScheduleSourceStatus metadata={activeProject?.scheduleSource} />
      </div>

      <CashFlowPlanningSourceCard
        policy={activeProject.cashFlowPlanning}
        projectId={activeProject._id}
        onUpdated={() => selectProject(activeProject._id)}
      />

      {(!activeProject.cashFlowPlanning
        || shouldShowDisbursementCoverage(activeProject.cashFlowPlanning)) ? (
          <DisbursementCoverageCard
            coverage={activeProject.disbursementCoverage}
            projectId={activeProject._id}
          />
        ) : null}

      {hasUnavailableExtraValues ? (
        <div className="alert alert-error margin-top-24" role="alert">
          <h3>Estouros / Economias indisponíveis</h3>
          <p>Reduza o período ou atualize os dados e tente novamente.</p>
        </div>
      ) : null}

      <EscapeHandler func={closeItemDetailOffcanvas} />

      {aiHighlight ? (
        <section className="ai-projection-highlight" role="status" aria-label="Ação da IA">
          <span>
            Competência afetada pela ação da IA: {selectedCompetenceLabel(aiHighlight.competence)?.replace(" selecionado", "")}.
          </span>
          <button
            type="button"
            aria-label="Fechar destaque da ação da IA"
            onClick={() => setAiHighlight(null)}
          >
            Fechar
          </button>
        </section>
      ) : null}


      <div className="grid gap-24 margin-top-40">
        <div>
          <hgroup className="dashboard-title flex wrap-wrap gap-16 align-center justify-content-space-between">
            <div className="flex gap-16 graph-labels">
              {/* <h2 className={selectedGraph === 'projectionGraph' ? 'active-graph' : ''} onClick={() => handleGraph(0)}>Projeção Financeira</h2> */}
              <h2 className={!projectionTable ? 'active-graph' : ''} onClick={() => updateProjectionTableView(false)}>Valores totais</h2>
              <h2 className={projectionTable ? 'active-graph' : ''} onClick={() => updateProjectionTableView(true)}>valores por mês</h2>
            </div>
            <DateRangeFilter />
          </hgroup>
        </div>
        <div className="flex" style={{ zIndex: '2' }}>
          <h2>Excluir documentos</h2>
          <Select
            style={{ width: '100%' }}
            isMulti
            isClearable={false}
            value={docs.map(i => ({ value: i, label: i }))}
            name="creditors"
            options={uniqueDocs.map(i => ({ value: i, label: i }))}
            onChange={handleFilter}
            components={{ Option: CustomOption }}
            className="basic-multi-select full-width"
            classNamePrefix="select"
            placeholder='Filtrar documentos'
          />
        </div>
        <div id="chart-bars">
          {!projectionTable && <FinancialBarChart />}
        </div>
      </div>


      <div className="financial-month-ai-selection margin-top-24">
        <hgroup className="dashboard-title">
          <h2>Análise mensal com IA</h2>
          {aiCompetence ? (
            <p>{selectedCompetenceLabel(aiCompetence)}</p>
          ) : (
            <p>Escolha um mês para comparar planejamento e realizado — não altera o período nem o gráfico.</p>
          )}
        </hgroup>
        <div className="flex gap-8">
          {showMonthAi && aiCompetence ? (
            <button
              type="button"
              className="financial-month-ai-action financial-month-ai-action--secondary"
              onClick={() => setShowMonthPicker(true)}
            >
              Trocar mês
            </button>
          ) : null}
          <button
            type="button"
            className="financial-month-ai-action"
            aria-expanded={showMonthAi}
            onClick={() => (
              showMonthAi
                ? setShowMonthAi(false)
                : (aiCompetence ? setShowMonthAi(true) : setShowMonthPicker(true))
            )}
          >
            {showMonthAi
              ? "Ocultar análise com IA"
              : presentationReadOnly ? "Ver análise disponível" : "Analisar mês com IA"}
          </button>
        </div>
      </div>

      {showMonthPicker ? (
        <FinancialMonthPickerDialog
          competences={availableCompetences(activeProject)}
          value={aiCompetence}
          onConfirm={(competence) => {
            setAiCompetence(competence);
            setShowMonthAi(true);
            setShowMonthPicker(false);
          }}
          onClose={() => setShowMonthPicker(false)}
        />
      ) : null}

      {showMonthAi && aiCompetence ? (
        <FinancialMonthAiPanel
          workId={activeProject._id}
          competence={aiCompetence}
          project={activeProject}
          onClose={() => setShowMonthAi(false)}
        />
      ) : null}

      {/* <div className="dashboard-title">
        <div className="flex gap-16 graph-labels">
          <h2 className={!projectionTable ? 'active-graph' : ''} onClick={() => updateProjectionTableView(false)}>Valores totais</h2>
          <h2 className={projectionTable ? 'active-graph' : ''} onClick={() => updateProjectionTableView(true)}>valores por mês</h2>
        </div>
      </div> */}


      {barChartDate ? (
        <section className="projection-scope-badge" role="status" aria-label="Escopo da tabela">
          <span>
            Tabela filtrada por competência: {selectedCompetenceLabel(financialCompetence(barChartDate))?.replace(" selecionado", "")} — os valores abaixo ignoram o período selecionado.
          </span>
          <button type="button" onClick={() => setBarChartDate()}>
            Limpar filtro
          </button>
        </section>
      ) : null}

      {projectionTable ? <Table /> : <UnitAccordion />}
      {/* {selectGraph === 'advancementGraph' ? <> </> : } */}



    </>

  );
};

export default Projection;
